'use client';

import { memo, useCallback, useMemo } from 'react';
import { useClearRefinements, useInstantSearch } from 'react-instantsearch';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useFilterState from '@/hooks/useFilterState';
import { CommonEnum } from '@/types/translations/CommonEnum';
import {
  Filter,
  Wine,
  MapPin,
  Tag,
  Globe,
  TrendingUp,
  X,
  Loader2,
  Building2,
  Euro,
  CalendarClock,
} from 'lucide-react';
import DefaultButton from '@/components/common/default/DefaultButton';
import FilterSection from '@/components/skus/FilterSkus/FilterSection';
import PriceRangeFilter from '@/components/skus/FilterSkus/PriceRangeFilter';
import SearchableRefinementListFilter from '@/components/skus/FilterSkus/SearchableRefinementListFilter';
import ToggleFilter from '@/components/skus/FilterSkus/ToggleFilter';
import SearchableMenuFilter from './SearchableMenuFilter';
import SearchableRefinementBrandListFilter from '@/components/skus/FilterSkus/SearchableRefinementBrandListFilter';
import { FilterConfig } from '@/types/props/components/skus';

const FILTER_SECTIONS = {
  STATUS: 'status',
  CATEGORY: 'category',
  BRAND: 'brand',
  PRICE: 'price',
  COUNTRY: 'country',
  AGE: 'age',
  REGION: 'region',
  ALCOHOL: 'alcohol',
  SIZE: 'size',
  YEAR: 'year',
} as const;

interface FilterSkusProps {
  disableRedirect?: boolean;
  filterConfig?: FilterConfig;
  accentColor?: string;
  secondaryAccentColor?: string;
}

const FilterSkus = ({
  disableRedirect = false,
  filterConfig = {},
  accentColor = 'slate-600',
  secondaryAccentColor = 'tw-bg-platinum-gray/50',
}: FilterSkusProps) => {
  const { translate } = useTypedTranslation<CommonEnum>('common');

  // Default filter configuration - all filters enabled by default
  const defaultFilterConfig: FilterConfig = {
    status: true,
    category: true,
    brand: true,
    price: true,
    country: true,
    region: true,
    size: true,
    year: true,
  };

  // Merge user config with defaults
  const mergedFilterConfig = { ...defaultFilterConfig, ...filterConfig };

  const { refine: clearRefinements, canRefine: canClearRefinements } = useClearRefinements();
  const { status } = useInstantSearch();

  // Use the custom hook for filter state management
  const {
    openSections,
    toggleSection,
    hasActiveRefinements,
    activeRefinementsCount,
    isLoading: filterStateLoading,
  } = useFilterState({
    autoCollapseOnMobile: true,
  });

  const isLoading = useMemo(
    () => filterStateLoading || status === 'loading' || status === 'stalled',
    [filterStateLoading, status]
  );

  const handleClearAll = useCallback(() => {
    if (canClearRefinements) {
      try {
        clearRefinements();
      } catch (error) {
        console.error('Clear refinements error:', error);
      }
    }
  }, [clearRefinements, canClearRefinements]);

  return (
    <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-gray-300/60 tw-bg-white/80 tw-shadow-md tw-backdrop-blur-sm">
      {/* Enhanced Header */}
      <div className="tw-border-b tw-border-platinum-gray/50 tw-bg-gradient-to-r tw-from-gray-50 tw-to-platinum-gray/30 tw-px-4 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div
            className={`tw-rounded-xl tw-p-3 tw-shadow-lg ${accentColor === 'slate-600' ? 'tw-bg-gradient-to-br tw-from-darkblue tw-to-text' : `tw-bg-${accentColor}`}`}
          >
            <Filter className="tw-h-6 tw-w-6 tw-text-white" />
          </div>
          <div className="tw-flex-1">
            {/* Not a heading: this is the filter-panel label, not part of the page
                outline. Using a heading here injected an <h3> before the page's
                <h2> sections (h1 → h3 → h2 skip). The panel already exposes an
                aria-label below. See WEB-1000. */}
            <p className="tw-text-xl tw-font-bold tw-text-textBlack">
              {translate(CommonEnum.filter) || 'Filters'}
            </p>
            <p className="tw-text-sm tw-text-slate-gray">
              {hasActiveRefinements ? (
                <span className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-order-100 tw-text-xs tw-font-semibold tw-text-order-800">
                    {activeRefinementsCount}
                  </span>
                  {translate(CommonEnum.refinementCountVariable_filter_s_active).replace(
                    '{{refinementCountVariable}}',
                    activeRefinementsCount.toString()
                  )}
                </span>
              ) : (
                translate(CommonEnum.refine_your_search) || 'Refine your search'
              )}
            </p>
          </div>
          {isLoading && <Loader2 className="tw-h-5 tw-w-5 tw-animate-spin tw-text-slate-400" />}
        </div>
      </div>

      {/* Enhanced Filter Content */}
      <div
        className="tw-flex-1 tw-divide-y tw-divide-slate-100/50 tw-overflow-y-auto"
        aria-label={translate(CommonEnum.search_filters) || 'Search filters'}
        aria-describedby="filter-instructions"
      >
        <div id="filter-instructions" className="tw-sr-only">
          {translate(CommonEnum.use_the_following_filters_to_refine_your_search_results) ||
            'Use the following filters to refine your search results. Each filter section can be expanded or collapsed.'}
        </div>

        {/* Status Filter */}
        {mergedFilterConfig.status && (
          <FilterSection
            id={FILTER_SECTIONS.STATUS}
            title={translate(CommonEnum.status) || 'Status'}
            icon={<TrendingUp className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.STATUS]}
            onToggle={() => toggleSection(FILTER_SECTIONS.STATUS)}
            secondaryAccentColor={secondaryAccentColor}
          >
            <div className="tw-space-y-2 tw-px-3 tw-py-2" aria-label="Status filters">
              <ToggleFilter
                attribute="hasBid"
                label={translate(CommonEnum.has_bids) || 'Has Bids'}
              />
              <ToggleFilter
                attribute="hasAsk"
                label={translate(CommonEnum.has_asks) || 'Has Asks'}
              />
            </div>
          </FilterSection>
        )}

        {/* Category Filter */}
        {mergedFilterConfig.category && (
          <SearchableMenuFilter
            attribute="category.name"
            title={translate(CommonEnum.category) || 'Category'}
            icon={<Wine className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.CATEGORY]}
            onToggle={() => toggleSection(FILTER_SECTIONS.CATEGORY)}
            searchPlaceholder={translate(CommonEnum.search_categories) || 'Search categories...'}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Brand Filter */}
        {mergedFilterConfig.brand && (
          <SearchableRefinementBrandListFilter
            isOpen={openSections[FILTER_SECTIONS.BRAND]}
            onToggle={() => toggleSection(FILTER_SECTIONS.BRAND)}
            icon={<Building2 className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Price Filter */}
        {mergedFilterConfig.price && (
          <PriceRangeFilter
            attribute="lowestAsk"
            isOpen={openSections[FILTER_SECTIONS.PRICE]}
            onToggle={() => toggleSection(FILTER_SECTIONS.PRICE)}
            icon={<Euro className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Country Filter */}
        {mergedFilterConfig.country && (
          <SearchableRefinementListFilter
            attribute="brand.countryOfOrigin"
            title={translate(CommonEnum.country) || 'Country'}
            icon={<Globe className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.COUNTRY]}
            onToggle={() => toggleSection(FILTER_SECTIONS.COUNTRY)}
            searchPlaceholder={translate(CommonEnum.search_countries) || 'Search countries...'}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Region Filter */}
        {mergedFilterConfig.region && (
          <SearchableRefinementListFilter
            attribute="brand.region"
            title={translate(CommonEnum.region) || 'Region'}
            icon={<MapPin className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.REGION]}
            onToggle={() => toggleSection(FILTER_SECTIONS.REGION)}
            searchPlaceholder={translate(CommonEnum.search_regions) || 'Search regions...'}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Size Filter */}
        {mergedFilterConfig.size && (
          <SearchableRefinementListFilter
            attribute="bottleSizeInMl"
            title={translate(CommonEnum.bottle_size) || 'Size'}
            icon={<Tag className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.SIZE]}
            onToggle={() => toggleSection(FILTER_SECTIONS.SIZE)}
            searchPlaceholder={translate(CommonEnum.search_bottle_sizes) || 'Search sizes...'}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}

        {/* Year Filter — `distilledYear` is the 4-digit vintage; the indexer drops the 1970
            "unset" sentinel, so there is no bogus bucket to filter out here.

            ponytail: values come back count-descending like every other refinement list, not
            newest-first. Sorting the 20 fetched values by year would show "the newest of the 20
            most common years", which reads as a complete list and is not one — the search box
            covers "I want 1998". Revisit if the facet ever returns every year. */}
        {mergedFilterConfig.year && (
          <SearchableRefinementListFilter
            attribute="distilledYear"
            title={translate(CommonEnum.distilled_year) || 'Year'}
            icon={<CalendarClock className={`tw-h-5 tw-w-5 tw-text-${accentColor}`} />}
            isOpen={openSections[FILTER_SECTIONS.YEAR]}
            onToggle={() => toggleSection(FILTER_SECTIONS.YEAR)}
            searchPlaceholder={translate(CommonEnum.search_distilled_years) || 'Search years...'}
            disableRedirect={disableRedirect}
            secondaryAccentColor={secondaryAccentColor}
          />
        )}
      </div>

      {/* Enhanced Clear All Button */}
      <div className="tw-flex-shrink-0 tw-border-t tw-border-platinum-gray/50 tw-bg-gradient-to-r tw-from-gray-50 tw-to-platinum-gray/30 tw-px-4 tw-py-3">
        <DefaultButton
          onClick={handleClearAll}
          disabled={!hasActiveRefinements || !canClearRefinements}
          className="tw-group tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-platinum-gray tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 hover:tw-text-slate-900 hover:tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-400 focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          aria-label={`${translate(CommonEnum.clear_all_variableCount_active_filters).replace('{{variableCount}}', activeRefinementsCount.toString())}`}
        >
          <X className="tw-h-4 tw-w-4 tw-transition-transform group-hover:tw-rotate-90" />
          {hasActiveRefinements ? (
            <>
              <div className="tw-h-4 tw-w-4 tw-rounded-full tw-bg-gradient-to-r tw-from-bid tw-to-customGreen"></div>
              {`${translate(CommonEnum.clear_all_active_filters_button_count).replace('{{variableCount}}', activeRefinementsCount.toString())}`}
            </>
          ) : (
            `${translate(CommonEnum.clear_all_active_filters_button)}`
          )}
        </DefaultButton>
      </div>
    </div>
  );
};

export default memo(FilterSkus);
